import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Pagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardMedia,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Image as ImageIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL;

const QuestionsList = () => {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    area: '',
    topic: '',
    difficulty: '',
    status: '',
  });
  const [deleteDialog, setDeleteDialog] = useState({
    open: false,
    questionId: null,
  });
  const [previewDialog, setPreviewDialog] = useState({
    open: false,
    question: null,
  });

  useEffect(() => {
    loadQuestions();
  }, [page, filters]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      const params = new URLSearchParams({
        page,
        limit: 20,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, v]) => v !== '')
        ),
      });

      const response = await axios.get(`${API_URL}/api/questions?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setQuestions(response.data.questions);
      setTotalPages(response.data.totalPages);
    } catch (err) {
      console.error('Error loading questions:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
    setPage(1);
  };

  const handleDelete = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API_URL}/api/questions/${deleteDialog.questionId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDeleteDialog({ open: false, questionId: null });
      loadQuestions();
    } catch (err) {
      console.error('Error deleting question:', err);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'validated':
        return 'success';
      case 'pending':
        return 'warning';
      case 'rejected':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'validated':
        return 'Validada';
      case 'pending':
        return 'Pendiente';
      case 'rejected':
        return 'Rechazada';
      default:
        return status;
    }
  };

  const getDifficultyLabel = (difficulty) => {
    switch (difficulty) {
      case 'easy':
        return 'Fácil';
      case 'medium':
        return 'Medio';
      case 'hard':
        return 'Difícil';
      default:
        return difficulty;
    }
  };

  const getAreaLabel = (area) => {
    const labels = {
      'matematicas': 'Matemáticas',
      'lectura-critica': 'Lectura Crítica',
      'sociales': 'Sociales',
      'ciencias': 'Ciencias',
      'ingles': 'Inglés',
    };
    return labels[area] || area;
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">Gestión de Preguntas</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate('/teacher/questions/new')}
        >
          Nueva Pregunta
        </Button>
      </Box>

      {/* Filtros */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Área</InputLabel>
              <Select
                name="area"
                value={filters.area}
                onChange={handleFilterChange}
                label="Área"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="matematicas">Matemáticas</MenuItem>
                <MenuItem value="lectura-critica">Lectura Crítica</MenuItem>
                <MenuItem value="sociales">Sociales</MenuItem>
                <MenuItem value="ciencias">Ciencias</MenuItem>
                <MenuItem value="ingles">Inglés</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Tema"
              name="topic"
              value={filters.topic}
              onChange={handleFilterChange}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Dificultad</InputLabel>
              <Select
                name="difficulty"
                value={filters.difficulty}
                onChange={handleFilterChange}
                label="Dificultad"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="easy">Fácil</MenuItem>
                <MenuItem value="medium">Medio</MenuItem>
                <MenuItem value="hard">Difícil</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Estado</InputLabel>
              <Select
                name="status"
                value={filters.status}
                onChange={handleFilterChange}
                label="Estado"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="validated">Validada</MenuItem>
                <MenuItem value="pending">Pendiente</MenuItem>
                <MenuItem value="rejected">Rechazada</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabla de Preguntas */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>ID</TableCell>
              <TableCell>Pregunta</TableCell>
              <TableCell>Área</TableCell>
              <TableCell>Tema</TableCell>
              <TableCell>Dificultad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Imágenes</TableCell>
              <TableCell align="center">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  Cargando...
                </TableCell>
              </TableRow>
            ) : questions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  No hay preguntas
                </TableCell>
              </TableRow>
            ) : (
              questions.map((question) => (
                <TableRow key={question.id}>
                  <TableCell>{question.id}</TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        maxWidth: 300,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {question.question}
                    </Typography>
                  </TableCell>
                  <TableCell>{getAreaLabel(question.area)}</TableCell>
                  <TableCell>{question.topic || '-'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getDifficultyLabel(question.difficulty)}
                      size="small"
                      color={
                        question.difficulty === 'easy'
                          ? 'success'
                          : question.difficulty === 'medium'
                          ? 'info'
                          : 'error'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={getStatusLabel(question.status)}
                      size="small"
                      color={getStatusColor(question.status)}
                    />
                  </TableCell>
                  <TableCell>
                    {(question.context_image || question.question_image) && (
                      <IconButton
                        size="small"
                        onClick={() =>
                          setPreviewDialog({ open: true, question })
                        }
                      >
                        <ImageIcon fontSize="small" />
                      </IconButton>
                    )}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      size="small"
                      onClick={() =>
                        setPreviewDialog({ open: true, question })
                      }
                    >
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() =>
                        navigate(`/teacher/questions/edit/${question.id}`)
                      }
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() =>
                        setDeleteDialog({ open: true, questionId: question.id })
                      }
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Paginación */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(e, value) => setPage(value)}
          color="primary"
        />
      </Box>

      {/* Dialog de Confirmación de Eliminación */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, questionId: null })}
      >
        <DialogTitle>Confirmar Eliminación</DialogTitle>
        <DialogContent>
          ¿Estás seguro de que deseas eliminar esta pregunta? Esta acción no se
          puede deshacer.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, questionId: null })}
          >
            Cancelar
          </Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de Vista Previa */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, question: null })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Vista Previa de Pregunta</DialogTitle>
        <DialogContent>
          {previewDialog.question && (
            <Box>
              {/* Contexto */}
              {previewDialog.question.context_text && (
                <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Contexto:
                  </Typography>
                  <Typography variant="body2">
                    {previewDialog.question.context_text}
                  </Typography>
                </Box>
              )}

              {/* Imagen de Contexto */}
              {previewDialog.question.context_image && (
                <Card sx={{ mb: 2 }}>
                  <CardMedia
                    component="img"
                    image={previewDialog.question.context_image}
                    alt="Context"
                    sx={{ maxHeight: 300, objectFit: 'contain' }}
                    onError={(e) => {
                      console.error('Error loading context image:', previewDialog.question.context_image);
                      e.target.style.display = 'none';
                    }}
                  />
                </Card>
              )}

              {/* Pregunta */}
              <Typography variant="h6" gutterBottom>
                {previewDialog.question.question}
              </Typography>

              {/* Imagen de Pregunta */}
              {previewDialog.question.question_image && (
                <Card sx={{ mb: 2 }}>
                  <CardMedia
                    component="img"
                    image={previewDialog.question.question_image}
                    alt="Question"
                    sx={{ maxHeight: 300, objectFit: 'contain' }}
                    onError={(e) => {
                      console.error('Error loading question image:', previewDialog.question.question_image);
                      e.target.style.display = 'none';
                    }}
                  />
                </Card>
              )}

              {/* Opciones */}
              <Box sx={{ mb: 2 }}>
                {previewDialog.question.options.map((option, index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1,
                      mb: 1,
                      border: 1,
                      borderColor:
                        String.fromCharCode(65 + index) ===
                        previewDialog.question.correct_answer
                          ? 'success.main'
                          : 'grey.300',
                      borderRadius: 1,
                      bgcolor:
                        String.fromCharCode(65 + index) ===
                        previewDialog.question.correct_answer
                          ? 'success.50'
                          : 'transparent',
                    }}
                  >
                    <Typography variant="body2">
                      <strong>{String.fromCharCode(65 + index)}.</strong> {option}
                    </Typography>
                  </Box>
                ))}
              </Box>

              {/* Respuesta Correcta */}
              <Box sx={{ p: 2, bgcolor: 'success.50', borderRadius: 1, mb: 2 }}>
                <Typography variant="subtitle2">
                  Respuesta Correcta: {previewDialog.question.correct_answer}
                </Typography>
              </Box>

              {/* Explicación */}
              {previewDialog.question.explanation && (
                <Box sx={{ p: 2, bgcolor: 'info.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Explicación:
                  </Typography>
                  <Typography variant="body2">
                    {previewDialog.question.explanation}
                  </Typography>
                </Box>
              )}

              {/* Metadata */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={`Área: ${getAreaLabel(previewDialog.question.area)}`} size="small" />
                {previewDialog.question.topic && (
                  <Chip label={`Tema: ${previewDialog.question.topic}`} size="small" />
                )}
                <Chip
                  label={getDifficultyLabel(previewDialog.question.difficulty)}
                  size="small"
                  color={
                    previewDialog.question.difficulty === 'easy'
                      ? 'success'
                      : previewDialog.question.difficulty === 'medium'
                      ? 'info'
                      : 'error'
                  }
                />
                <Chip
                  label={getStatusLabel(previewDialog.question.status)}
                  size="small"
                  color={getStatusColor(previewDialog.question.status)}
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPreviewDialog({ open: false, question: null })}
          >
            Cerrar
          </Button>
          {previewDialog.question && (
            <Button
              variant="contained"
              onClick={() =>
                navigate(`/teacher/questions/edit/${previewDialog.question.id}`)
              }
            >
              Editar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default QuestionsList;
